/**
 * customers-api — typed wrappers for the Customers data layer, through the
 * @v0y4ge/api-client `apiRaw` choke point (same seam as journeys-api / wf-api).
 *
 * DECLARED and pending in @v0y4ge/api-contracts / api-mocks — Claude Code owns
 * that layer (REQ-069; spec: specs/customers-seed-package-2026-07-24.md). Until
 * the real ops land, the preview harness serves them from CD preview seeds
 * (`__source: "cd-preview-seed"`), surfaced per screen via <DataSourceStrip>.
 * Records are per-tenant (retail / banking / higher-ed) and NEVER authored in
 * app/src — they come back from listCustomers.
 *
 * Every mutation awaits its typed op (no-fake-interactivity): the source
 * prototype fired toasts without persisting; here the modal confirm awaits the
 * op and only then toasts. When the real ops ship, these chips disappear with
 * zero UI change.
 */
import { apiRaw } from "@v0y4ge/api-client";
import { reportOpSource } from "../../shell/opSource";
import type { CustomerRecord } from "./customers-config";

const tag = <T,>(op: string, p: Promise<T>): Promise<T> =>
  p.then((r) => { reportOpSource(op, r); return r; });
const json = (body: unknown): RequestInit => ({ method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });

export const listCustomers = (tenantSlug?: string | null) =>
  tag("listCustomers", apiRaw<{ customers: CustomerRecord[] }>(`/v1/admin/customers${tenantSlug ? `?tenant=${encodeURIComponent(tenantSlug)}` : ""}`, undefined, 'listCustomers'));

export interface MessageInput { channel: "email" | "sms"; to: string; subject?: string; body: string; }
export const messageCustomer = (id: string, input: MessageInput) =>
  tag("messageCustomer", apiRaw<{ ok: boolean }>(`/v1/admin/customers/${encodeURIComponent(id)}/message`, json(input), 'messageCustomer'));

export interface BookInput { service: string; date: string; time: string; }
/** SUPERSEDED 2026-07-26 (APT-02b): CABook now books through the canonical
 * appointments family (bookAppointment, REQ-089); this REQ-069 op is kept for
 * traceability until the kit confirms the alias retirement. Unused in UI. */
export const bookCustomerAppointment = (id: string, input: BookInput) =>
  tag("bookCustomerAppointment", apiRaw<{ ok: boolean; appointmentId?: string }>(`/v1/admin/customers/${encodeURIComponent(id)}/appointments`, json(input), 'bookCustomerAppointment'));

export interface RegisterInput { eventId: string; eventTitle: string; party: number; }
export const registerCustomerForEvent = (id: string, input: RegisterInput) =>
  tag("registerCustomerForEvent", apiRaw<{ ok: boolean; registrationId?: string }>(`/v1/admin/customers/${encodeURIComponent(id)}/registrations`, json(input), 'registerCustomerForEvent'));

/** Contextual history-item actions from the 360 drawer (reschedule, cancel,
 *  release a wait-list hold, offer a slot, find similar…) — one typed op. */
export interface ItemActionInput { kind: string; action: string; ref?: string | null; }
export const customerItemAction = (id: string, input: ItemActionInput) =>
  tag("customerItemAction", apiRaw<{ ok: boolean }>(`/v1/admin/customers/${encodeURIComponent(id)}/activity`, json(input), 'customerItemAction'));
